<template>
  <div>
    <finished-documentation
      v-if="!isValidStatusToSubmitDocumentation || sucessSubmitFile"
    />
    <div
      class="w-full flex justify-center"
      v-else
    >
      <jade-page-container size="wide">
        <jade-box class="lg:flex-[2]">
          <div class="flex flex-col gap-3 p-5">
            <jade-text
              variant="heading-xsmall"
              color="high"
              >{{ $t('send.document') }}</jade-text
            >
            <jade-text
              color="medium"
              weight="medium"
              >{{ $t('paragraph.1') }}
              <span class="text-jade-content-high">{{
                $t('paragraph.2')
              }}</span></jade-text
            >
            <jade-link
              external
              :href="$sanitize(chargebackDocumentationUrl)"
              target="_blank"
              >{{ $t('documents-list.link') }}</jade-link
            >
            <file-drag-and-drop
              :files.sync="files"
              :limit="50"
            />
            <file-list-item
              v-for="(fileItem, index) in filesItems"
              :key="`${fileItem.name}-${index}`"
              :file="fileItem"
              :fileIsLoading="filesIsLoading"
              @deleteFile="deleteFile"
            />
          </div>
        </jade-box>
        <template #aside>
          <div class="flex flex-col gap-6">
            <representment-resume />
            <jade-alert
              v-if="totalSizeError"
              variant="negative"
              showIcon
            >
              {{ $t('total.size.error') }}
            </jade-alert>
            <jade-button
              variant="primary-solid"
              @click="submitFile"
              :loading="submitFileIsLoading"
              :disabled="sendDocumentationIsDisabled"
              >{{ $t('send.documentation') }}</jade-button
            >
          </div>
        </template>
      </jade-page-container>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import {
  JadeButton,
  JadeText,
  JadeBox,
  JadeLink,
  JadePageContainer,
  JadeAlert
} from '@jade'
import RepresentmentResume from '@/views/Chargebacks/Representment/components/RepresentmentResume'
import FileDragAndDrop from '@/views/Chargebacks/components/FileDragAndDrop'
import FileListItem from '../components/FileListItem.vue'

import ChargebackManager from '../chargeback.manager'
import FinishedDocumentation from '../components/FinishedDocumentation.vue'

import { EXTERNAL_ENDPOINTS } from '@/external-endpoints'

export default {
  name: 'ChargeBackRepresentment',
  components: {
    JadeButton,
    JadeText,
    JadeBox,
    JadeLink,
    JadePageContainer,
    FileDragAndDrop,
    RepresentmentResume,
    FileListItem,
    FinishedDocumentation,
    JadeAlert
  },
  data () {
    return {
      files: [],
      filesItems: null,
      filesIsLoading: false,
      submitFileIsLoading: false,
      sucessSubmitFile: false,
      totalSizeError: false,
      hasAnyError: false
    }
  },
  computed: {
    ...mapGetters(['chargeback']),
    hasAnyFile () {
      return this.files.length > 0
    },
    sendDocumentationIsDisabled () {
      return !this.hasAnyFile || this.filesIsLoading || !!this.hasAnyError
    },
    isValidStatusToSubmitDocumentation () {
      return ChargebackManager.isValidStatusToSubmitDocumentation(
        this.chargeback.status.code
      )
    },
    chargebackDocumentationUrl () {
      return EXTERNAL_ENDPOINTS.CHARGEBACK_RECOMMENDATION_DOC.url()
    }
  },
  watch: {
    files: {
      deep: true,
      handler (newFiles) {
        if (newFiles.length > 0) {
          this.sendFileToValidate(newFiles)
        }
      }
    }
  },

  methods: {
    ...mapActions({
      validateFile: 'VALIDATE_FILE',
      processFile: 'PROCESS_FILE'
    }),
    deleteFile (fileToRemove) {
      const fileIndex = this.files.findIndex(
        file => file.name === fileToRemove.name
      )
      if (fileIndex !== -1) {
        this.files.splice(fileIndex, 1)
        this.filesItems.splice(fileIndex, 1)
      }
    },
    processValidateResponse (validateResponse) {
      this.totalSizeError = !validateResponse.is_valid_size
      this.hasAnyError =
        !!validateResponse.validation_error.length ||
        this.totalSizeError ||
        !validateResponse.files_are_valid
      this.filesItems = ChargebackManager.getFilesWithErrors(
        this.files,
        validateResponse.files
      )
    },
    sendFileToValidate () {
      this.filesIsLoading = true
      const filesToValidate = ChargebackManager.prepareFilesToValidate(
        this.files
      )

      this.filesItems = this.files.map(file => {
        return {
          name: file.name
        }
      })
      this.validateFile({
        transactionId: this.chargeback.transactionId,
        files: filesToValidate
      })
        .then(this.processValidateResponse)
        .catch(this.processValidateResponse)
        .finally(() => (this.filesIsLoading = false))
    },
    submitFile () {
      this.submitFileIsLoading = true
      this.processFile({
        transactionId: this.chargeback.transactionId,
        files: this.files
      })
        .then(() => {
          this.sucessSubmitFile = true
        })
        .finally(() => {
          this.submitFileIsLoading = false
        })
    }
  }
}
</script>
<i18n>
{
  "pt-BR": {
    "send.document": "Enviar documento",
    "paragraph.1": "Para abrir a disputa é necessário que você",
    "paragraph.2": "envie uma documentação, comprovando de forma clara que o motivo alegado na contestação está equivocada.",
    "documents-list.link": "Confira todas as recomendações para o envio da documentação",
    "send.documentation": "Enviar documentação",
    "total.size.error": "Os documentos anexados não podem exceder o limite de tamanho combinado de 50MB."
  },
  "en-US": {
    "send.document": "Send document",
    "paragraph.1": "To open the dispute it is necessary that you",
    "paragraph.2": "send documentation, clearly proving that the reason alleged in the dispute is wrong.",
    "documents-list.link": "Check all recommendations for sending documentation",
    "send.documentation": "Send documentation",
    "total.size.error": "The attached documents cannot exceed the combined size limit of 50MB."
  }
}
</i18n>
